import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createGroundTrip,
  deleteGroundTrip,
  updateGroundTrip,
  getGroundTrips,
} from '../services/api/groundTripsApi';
import type { GroundMode, GroundTrip } from '../types/ground';
import {
  emptyTrip,
  THEMES,
  minutesToFormatted,
  buildGroundEditPayload,
  groundEditDelta,
} from '../components/ground-trip/groundTripUtils';

export function useGroundTripService(mode: GroundMode, isAuthenticated: boolean) {
  const [trips, setTrips] = useState<GroundTrip[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<Partial<GroundTrip>>(() => emptyTrip(mode));
  const [editOriginal, setEditOriginal] = useState<GroundTrip | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const perPage = 10;

  const load = useCallback(async () => {
    if (!isAuthenticated) {
      setTrips([]);
      return;
    }
    setLoading(true);
    try {
      const rows = await getGroundTrips(mode);
      setTrips(Array.isArray(rows) ? rows : []);
    } catch {
      setTrips([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, mode]);

  useEffect(() => {
    void load();
  }, [load]);

  const openNew = () => {
    setIsEditing(false);
    setEditOriginal(null);
    setDraft(emptyTrip(mode));
    setShowForm(true);
  };

  const openEdit = (trip: GroundTrip) => {
    setIsEditing(true);
    setEditOriginal(trip);
    setDraft({
      ...trip,
      formattedDuration: trip.formattedDuration || minutesToFormatted(trip.duration),
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setIsEditing(false);
    setEditOriginal(null);
    setDraft(emptyTrip(mode));
  };

  const save = async () => {
    if (isEditing && draft.id != null && editOriginal) {
      const delta = groundEditDelta(mode, editOriginal, draft);
      if (Object.keys(delta).length) {
        await updateGroundTrip(draft.id, delta);
      }
    } else {
      await createGroundTrip(buildGroundEditPayload(mode, draft) as Omit<GroundTrip, 'id'>);
    }
    closeForm();
    await load();
  };

  const remove = async (trip: GroundTrip) => {
    await deleteGroundTrip(trip.id);
    if (selectedId === trip.id) setSelectedId(null);
    await load();
  };

  const patchTrip = useCallback((patched: GroundTrip) => {
    setTrips(prev => prev.map(t => (t.id === patched.id ? { ...t, ...patched } : t)));
  }, []);

  const totalPages = Math.max(1, Math.ceil(trips.length / perPage));
  const pageTrips = useMemo(() => {
    const start = (page - 1) * perPage;
    return trips.slice(start, start + perPage);
  }, [trips, page]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return {
    trips,
    pageTrips,
    loading,
    showForm,
    setShowForm,
    isEditing,
    draft,
    setDraft,
    selectedId,
    setSelectedId,
    page,
    setPage,
    totalPages,
    perPage,
    openNew,
    openEdit,
    closeForm,
    save,
    remove,
    patchTrip,
    reload: load,
    theme: THEMES[mode],
  };
}
